import React from 'react';
import type { Component } from '../schemas/validation';
import { ButtonLink } from './shared/ButtonLink';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getThemeStyles } from '../styles/themes';
import { getFontFamily } from '../utils/fontLoader';

interface TextBlocksComponentProps {
  component: Component;
}

export const TextBlocksComponent: React.FC<TextBlocksComponentProps> = ({ component }) => {
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const brandFonts = useBrandFonts();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Type guard to ensure we have a text-blocks component
  if (component.type !== 'text-blocks') {
    return (
      <div className="w-full p-8 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600">Invalid component type for text-blocks component</p>
      </div>
    );
  }

  const { data } = component;

  // Validate required data
  if (!data?.blocks || !Array.isArray(data.blocks) || data.blocks.length === 0) {
    return (
      <div className="w-full p-8 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-yellow-600">No text blocks to display</p>
      </div>
    );
  }

  // Extract data with defaults from the text-blocks schema
  const {
    componentTitle,
    componentSubtitle,
    componentDescription,
    blocks = [],
    layout = 'vertical',
    columns = '2',
    gap = 'md',
    horizontalPadding = 'md',
    headerAlignment = 'center',
    globalAlignment = 'left',
    backgroundColor,
    defaultTitleSize = 'lg',
    defaultContentSize = 'base',
  } = data;

  // Get gap classes for grid/flex layouts
  const getGapClasses = () => {
    const gapMap: Record<string, string> = {
      none: 'gap-0',
      xs: 'gap-2',
      sm: 'gap-4',
      md: 'gap-6',
      lg: 'gap-8',
      xl: 'gap-10',
      '2xl': 'gap-12',
    };
    return gapMap[gap] || gapMap.md;
  };

  // Get vertical spacing classes for vertical layout
  const getVerticalSpacingClasses = () => {
    const spaceMap: Record<string, string> = {
      none: 'space-y-0',
      xs: 'space-y-2',
      sm: 'space-y-4',
      md: 'space-y-6',
      lg: 'space-y-8',
      xl: 'space-y-10',
      '2xl': 'space-y-12',
    };
    return spaceMap[gap] || spaceMap.md;
  };

  // Get column gap classes for columns/masonry layout
  const getColumnGapClasses = () => {
    const columnGapMap: Record<string, string> = {
      none: 'gap-x-0',
      xs: 'gap-x-2',
      sm: 'gap-x-4',
      md: 'gap-x-6',
      lg: 'gap-x-8',
      xl: 'gap-x-10',
      '2xl': 'gap-x-12',
    };
    return columnGapMap[gap] || columnGapMap.md;
  };

  // Get horizontal padding classes for container
  const getHorizontalPaddingClasses = () => {
    const paddingMap: Record<string, string> = {
      none: 'px-0',
      xs: 'px-2',
      sm: 'px-4',
      md: 'px-4 sm:px-6 lg:px-8',
      lg: 'px-6 sm:px-8 lg:px-12',
      xl: 'px-8 sm:px-12 lg:px-16',
      '2xl': 'px-12 sm:px-16 lg:px-24',
    };
    return paddingMap[horizontalPadding] || paddingMap.md;
  };

  // Get layout classes
  const getLayoutClasses = () => {
    switch (layout) {
      case 'grid':
        return `grid ${getGapClasses()} grid-cols-1 ${
          columns === '2'
            ? 'md:grid-cols-2'
            : columns === '3'
              ? 'md:grid-cols-2 lg:grid-cols-3'
              : columns === '4'
                ? 'md:grid-cols-2 lg:grid-cols-4'
                : columns === '5'
                  ? 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'
                  : columns === '6'
                    ? 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6'
                    : ''
        } [&>*]:min-w-0`;
      case 'masonry':
        return `columns-1 ${
          columns === '2'
            ? 'md:columns-2'
            : columns === '3'
              ? 'md:columns-2 lg:columns-3'
              : columns === '4'
                ? 'md:columns-2 lg:columns-4'
                : ''
        } ${getColumnGapClasses()}`;
      case 'vertical':
      default:
        return getVerticalSpacingClasses();
    }
  };

  // Get alignment classes for text blocks
  const getAlignmentClasses = () => {
    switch (globalAlignment) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      case 'justify':
        return 'text-justify';
      case 'left':
      default:
        return 'text-left';
    }
  };

  // Get alignment classes for header (title/subtitle/description)
  const getHeaderAlignmentClasses = () => {
    switch (headerAlignment) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      case 'justify':
        return 'text-justify';
      case 'left':
      default:
        return 'text-left';
    }
  };

  // Get title size classes
  const getTitleSizeClasses = (size?: string) => {
    const titleSize = size || defaultTitleSize;
    switch (titleSize) {
      case 'xs':
        return 'text-xs';
      case 'sm':
        return 'text-sm';
      case 'base':
        return 'text-base';
      case 'lg':
        return 'text-lg';
      case 'xl':
        return 'text-xl';
      case '2xl':
        return 'text-2xl';
      case '3xl':
        return 'text-3xl';
      case '4xl':
        return 'text-4xl';
      case '5xl':
        return 'text-5xl';
      default:
        return 'text-lg';
    }
  };

  // Get content size classes
  const getContentSizeClasses = (size?: string) => {
    const contentSize = size || defaultContentSize;
    switch (contentSize) {
      case 'xs':
        return 'text-xs';
      case 'sm':
        return 'text-sm';
      case 'base':
        return 'text-base';
      case 'lg':
        return 'text-lg';
      case 'xl':
        return 'text-xl';
      case '2xl':
        return 'text-2xl';
      default:
        return 'text-base';
    }
  };

  // Color priority: component.customColors > deprecated fields > theme colors
  const titleColorTheme =
    (data as any).customColors?.title || themeStyles.textBlocks?.titleColor || '#111827';
  const subtitleColorTheme =
    (data as any).customColors?.subtitle || themeStyles.textBlocks?.subtitleColor || '#6b7280';
  const textColorTheme =
    (data as any).customColors?.text || themeStyles.textBlocks?.textColor || '#6b7280';
  const linkColorTheme =
    (data as any).customColors?.link || themeStyles.textBlocks?.linkColor || '#3b82f6';
  const bgColorTheme =
    (data as any).customColors?.background ||
    backgroundColor ||
    themeStyles.textBlocks?.background ||
    '#ffffff';

  // Button colors: component customColors > theme colors > fallback
  const buttonBgColor =
    data.customColors?.buttonBg ||
    themeStyles.textBlocks?.buttonBg ||
    themeStyles.cta?.buttonBg ||
    '#3b82f6';
  const buttonTextColor =
    data.customColors?.buttonText ||
    themeStyles.textBlocks?.buttonText ||
    themeStyles.cta?.buttonText ||
    '#ffffff';

  // Get button styles based on button type
  const getButtonStyles = (style: string) => {
    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      default:
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
    }
  };

  // Render buttons helper
  const renderButtons = () => {
    const buttons = (data as any).buttons;
    if (!buttons || buttons.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-4 mt-12 justify-center">
        {buttons.map((button: any, index: number) => (
          <ButtonLink
            key={index}
            url={button.url}
            target={button.target}
            className="px-6 py-3 font-semibold rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-md inline-block text-center"
            style={getButtonStyles(button.style)}
          >
            {button.text}
          </ButtonLink>
        ))}
      </div>
    );
  };

  // Render a single text block
  const renderTextBlock = (block: any, index: number) => {
    const { id, title, content } = block;

    // Add break-inside-avoid for masonry/columns layouts to keep title and content together
    const needsBreakAvoid = layout === 'masonry' || layout === 'columns';

    return (
      <div
        key={id || index}
        className={`text-block min-w-0 ${needsBreakAvoid ? 'break-inside-avoid mb-4' : ''}`}
      >
        {title && (
          <h3
            className={`${getTitleSizeClasses()} font-semibold mb-3`}
            style={{ color: titleColorTheme, ...headingFontStyle }}
          >
            {title}
          </h3>
        )}
        {content && (
          <>
            <style>{`
              .text-block-content-${component.id}-${index}, .text-block-content-${component.id}-${index} * {
                color: ${textColorTheme} !important;
              }
            `}</style>
            <div
              className={`text-block-content-${component.id}-${index} rich-text-content ${getContentSizeClasses()} leading-relaxed`}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </>
        )}
      </div>
    );
  };

  // Build container styles
  const containerStyles: React.CSSProperties = {
    backgroundColor: bgColorTheme,
  };

  return (
    <section className="py-16 md:py-24" style={containerStyles}>
      <style>
        {`
          .text-block a {
            color: ${linkColorTheme};
            text-decoration: underline;
          }
          .text-block a:hover {
            color: ${themeStyles.textBlocks?.linkHover || linkColorTheme};
          }
        `}
      </style>
      <div className={`max-w-7xl mx-auto min-w-0 ${getHorizontalPaddingClasses()}`}>
        {/* Component Header */}
        {(componentTitle || componentSubtitle || componentDescription) && (
          <div className={`mb-12 ${getHeaderAlignmentClasses()}`}>
            {/* Component Subtitle */}
            {componentSubtitle && (
              <p
                className="text-lg md:text-xl font-medium mb-4 opacity-80"
                style={{ color: subtitleColorTheme, ...bodyFontStyle }}
              >
                {componentSubtitle}
              </p>
            )}

            {/* Component Title */}
            {componentTitle && (
              <h2
                className="text-3xl md:text-4xl font-bold mb-6 leading-tight"
                style={{ color: titleColorTheme, ...headingFontStyle }}
              >
                {componentTitle}
              </h2>
            )}

            {/* Component Description */}
            {componentDescription && (
              <>
                <style>{`
                  .text-blocks-description-${component.id}, .text-blocks-description-${component.id} * {
                    color: ${textColorTheme} !important;
                  }
                `}</style>
                <div
                  className={`text-blocks-description-${component.id} rich-text-content text-lg leading-relaxed max-w-3xl mb-8 ${headerAlignment === 'center' ? 'mx-auto' : ''}`}
                  dangerouslySetInnerHTML={{ __html: componentDescription }}
                />
              </>
            )}
          </div>
        )}

        {/* Text Blocks */}
        <div className={`${getLayoutClasses()} ${getAlignmentClasses()}`}>
          {blocks.map((block, index) => renderTextBlock(block, index))}
        </div>

        {/* Call-to-Action Buttons */}
        {renderButtons()}
      </div>
    </section>
  );
};
